import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { DollarSignIcon, PuzzleIcon, CheckCircle2Icon, XCircleIcon } from "lucide-react";

interface ExpenseBlock {
  id: number;
  name: string;
  size: number;
  isEssential: boolean;
  amount: number;
}

const initialBlocks: ExpenseBlock[] = [
  { id: 1, name: "Rent", size: 3, isEssential: true, amount: 1200 },
  { id: 2, name: "Groceries", size: 2, isEssential: true, amount: 400 },
  { id: 3, name: "Savings", size: 2, isEssential: true, amount: 300 },
  { id: 4, name: "Entertainment", size: 1, isEssential: false, amount: 100 },
  { id: 5, name: "Transportation", size: 2, isEssential: true, amount: 200 },
];

const GRID_SIZE = 4;
const TOTAL_BUDGET = 2500;

export const BudgetGridlock = () => {
  const { toast } = useToast();
  const [grid, setGrid] = useState<(ExpenseBlock | null)[]>(Array(GRID_SIZE * GRID_SIZE).fill(null));
  const [availableBlocks, setAvailableBlocks] = useState<ExpenseBlock[]>(initialBlocks);
  const [selectedBlock, setSelectedBlock] = useState<ExpenseBlock | null>(null);
  const [score, setScore] = useState(0);
  const [totalSpent, setTotalSpent] = useState(0);

  const handleBlockSelect = (block: ExpenseBlock) => {
    setSelectedBlock(block);
  };

  const handleCellClick = (index: number) => {
    if (!selectedBlock) return;

    // Check if there's enough space
    const rowStart = Math.floor(index / GRID_SIZE);
    const colStart = index % GRID_SIZE;
    
    if (colStart + selectedBlock.size > GRID_SIZE) {
      toast({
        title: "Invalid placement",
        description: "Not enough space in this row",
        variant: "destructive",
      });
      return;
    }

    // Check if cells are empty
    for (let i = 0; i < selectedBlock.size; i++) {
      if (grid[index + i] !== null) {
        toast({
          title: "Invalid placement",
          description: "Space already occupied",
          variant: "destructive",
        });
        return;
      }
    }

    // Place the block
    const newGrid = [...grid];
    for (let i = 0; i < selectedBlock.size; i++) {
      newGrid[index + i] = selectedBlock;
    }
    setGrid(newGrid);

    // Remove block from available blocks
    setAvailableBlocks(prev => prev.filter(b => b.id !== selectedBlock.id));
    setSelectedBlock(null);

    // Update total spent
    const newTotal = totalSpent + selectedBlock.amount;
    setTotalSpent(newTotal);

    // Update score
    if (newTotal <= TOTAL_BUDGET) {
      setScore(prev => prev + (selectedBlock.isEssential ? 100 : 50));
    } else {
      toast({
        title: "Over budget!",
        description: "You've exceeded your monthly budget",
        variant: "destructive",
      });
    }
  };

  const resetGame = () => {
    setGrid(Array(GRID_SIZE * GRID_SIZE).fill(null));
    setAvailableBlocks(initialBlocks);
    setSelectedBlock(null);
    setScore(0);
    setTotalSpent(0);
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PuzzleIcon className="w-6 h-6 text-primary" />
          Budget Gridlock
        </CardTitle>
        <div className="flex justify-between items-center mt-2">
          <div className="text-sm text-muted-foreground">
            Budget: ${TOTAL_BUDGET} | Spent: ${totalSpent}
          </div>
          <div className="text-sm font-medium">Score: {score}</div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-4 gap-2">
          {grid.map((cell, index) => (
            <div
              key={index}
              onClick={() => handleCellClick(index)}
              className={`
                h-16 border rounded-lg flex items-center justify-center p-2 text-sm text-center
                transition-colors cursor-pointer
                ${cell ? 'bg-primary/10 border-primary' : 'hover:bg-secondary/10'}
                ${selectedBlock ? 'hover:bg-primary/5' : ''}
              `}
            >
              {cell?.name}
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium">Available Expenses:</h3>
          <div className="flex flex-wrap gap-2">
            {availableBlocks.map(block => (
              <Button
                key={block.id}
                variant={selectedBlock?.id === block.id ? "default" : "outline"}
                className={`flex items-center gap-2 ${block.isEssential ? 'border-primary' : ''}`}
                onClick={() => handleBlockSelect(block)}
              >
                <DollarSignIcon className="w-4 h-4" />
                {block.name} (${block.amount})
                {block.isEssential && (
                  <span className="text-xs bg-primary/10 text-primary px-1 rounded">Essential</span>
                )}
              </Button>
            ))}
          </div>
        </div>
      </CardContent>
      <CardFooter className="justify-between">
        <Button variant="outline" onClick={resetGame}>
          Reset Game
        </Button>
        {availableBlocks.length === 0 && (
          <div className="flex items-center gap-2">
            {totalSpent <= TOTAL_BUDGET ? (
              <>
                <CheckCircle2Icon className="w-5 h-5 text-green-500" />
                <span className="text-green-500 font-medium">Success! Budget maintained!</span>
              </>
            ) : (
              <>
                <XCircleIcon className="w-5 h-5 text-red-500" />
                <span className="text-red-500 font-medium">Over budget by ${totalSpent - TOTAL_BUDGET}</span>
              </>
            )}
          </div>
        )}
      </CardFooter>
    </Card>
  );
};